import { useRef } from "react";
import { m } from "@/paraglide/messages";
import { useTypingTest } from "../hooks/useTypingTest";
import { TextDisplay } from "./TextDisplay";
import { TypingResults } from "./TypingResults";

function fmtTime(sec: number): string {
  const s = Math.ceil(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="text-center">
      <div className="font-display text-2xl font-bold tabular-nums sm:text-3xl">
        {value}
      </div>
      <div className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

export function TypingTest({ durationSec }: { durationSec: number }) {
  const t = useTypingTest(durationSec);
  const taRef = useRef<HTMLTextAreaElement>(null);

  return (
    <div className="pct-card space-y-6 rounded-2xl p-6 sm:p-8">
      <div className="flex items-end justify-center gap-10" aria-live="polite">
        <Stat label={m.typing_stat_wpm()} value={Math.round(t.wpm)} />
        <Stat
          label={m.typing_stat_accuracy()}
          value={`${Math.round(t.accuracy)}%`}
        />
        <Stat label={m.typing_stat_time()} value={fmtTime(t.remaining)} />
      </div>

      {t.status === "done" ? (
        <TypingResults
          wpm={Math.round(t.wpm)}
          accuracy={Math.round(t.accuracy)}
          correct={t.correct}
          incorrect={t.incorrect}
          onRestart={() => {
            t.restart();
            requestAnimationFrame(() => taRef.current?.focus());
          }}
        />
      ) : (
        <div className="space-y-4">
          {t.text ? (
            <div className="pct-panel rounded-xl p-4 sm:p-5">
              <TextDisplay text={t.text} typed={t.typed} />
            </div>
          ) : (
            <p className="text-muted-foreground">{m.typing_loading()}</p>
          )}
          <textarea
            ref={taRef}
            // biome-ignore lint/a11y/noAutofocus: a typing test should be ready to type immediately.
            autoFocus
            aria-label={m.typing_input_label()}
            value={t.typed}
            onChange={(e) => t.onType(e.target.value)}
            disabled={!t.text}
            rows={3}
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
            placeholder={m.typing_input_placeholder()}
            className="w-full resize-none rounded-md border-black/15 bg-white p-3 font-mono text-base text-neutral-900 shadow-xs outline-none placeholder:text-neutral-500 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:border-black/15 dark:bg-white dark:text-neutral-900"
          />
        </div>
      )}
    </div>
  );
}
